import FilesList from 'components/v2/file-row/FilesList';
import FileUpload from 'components/v2/file-upload/FileUpload';
import { ButtonUploadIcon } from 'components/v2/icons';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import React, { ChangeEvent } from 'react';
import { ActionState, VerificationType } from 'services/EpdClient';
import { addEpdFile } from 'services/api/mutations';
import { useAddEpdVerificationReportFile, useRemoveEpdVerificationReportFile } from 'services/api/mutations';
import { useEpdVerificationReportInfo } from 'services/api/queries';
import styled from 'styled-components';
import { HyperLink } from 'styles/Styles.styled';
import { ActionFieldPanelFullWidth, FieldPanelHeader, TitleIconBar } from 'styles/v2/Styles.styled';
import { EPD_DEVELOPMENT_VERIFICATION_TEMPLATES_URL } from 'constants/constants';
import WizardCalendarControlled from '../epd-wizard-inputs/WizardCalendarControlled';
import WizardTextInputControlledComponent from '../epd-wizard-inputs/WizardTextInputControlled';
import { HelpBox } from '../help-boxes';
import { usePcrV2Loading } from './hooks/usePcrV2';
import SkeletonStep4 from './skeletons/SkeletonStep4';
import TextWithAnimatedEllipsis from '../file-upload/TextWithAnimatedEllipsis';
import { CompilerCompositeStatusModel } from 'types/types';
import { formatAsUTCDate } from 'util/utils';

const Step4: React.FunctionComponent<{
  epdId: string;
  epdVersionId: string;
  validationState?: CompilerCompositeStatusModel;
}> = ({ epdId, epdVersionId, validationState }) => {
  const addVerificationReportFileMutation = useAddEpdVerificationReportFile(epdVersionId);
  const removeEpdVerificationReportFileMutation = useRemoveEpdVerificationReportFile(epdVersionId);
  const verificationInfo = useEpdVerificationReportInfo(epdVersionId).data;
  const isPcrV2Loading = usePcrV2Loading();

  const isLoading = verificationInfo === undefined || isPcrV2Loading || validationState === undefined;
  if (isLoading) {
    return <SkeletonStep4 />;
  }

  const verifierInfoBlockActionState = validationState.fieldsState.verifierInfoBlock;
  const reportBlockVisibility = validationState.fieldsState.verificationReportBlock;
  const isReportBlockEditable = reportBlockVisibility === ActionState.Enabled;

  const showUploadBlock = () => {
    if (
      reportBlockVisibility == ActionState.Enabled ||
      reportBlockVisibility == ActionState.Disabled ||
      reportBlockVisibility == ActionState.Locked
    ) {
      return ActionState.Enabled;
    }
    return ActionState.Hidden;
  };

  const showStub = () => {
    return reportBlockVisibility == ActionState.Stub ? ActionState.Enabled : ActionState.Hidden;
  };

  const showStubVerifier = () => {
    return reportBlockVisibility == ActionState.StubVerifier ? ActionState.Enabled : ActionState.Hidden;
  };

  const uploadFile = async (e: ChangeEvent<HTMLInputElement>, propertyName: string) => {
    const target = e.target as HTMLInputElement;
    let file = target && target.files && target.files[0];
    if (!file) {
      return null;
    }

    await addEpdFile(
      { epdId, propertyName, file, documentTypeId: null, documentLanguageId: null },
      addVerificationReportFileMutation
    );
  };

  const handleRemoveVerificationReportFile = (fileId: string) => {
    removeEpdVerificationReportFileMutation.mutate(fileId);
  };

  const getApprovalTimestampCalendarValue = (date: Date | string | null | undefined) => {
    if (!date) {
      return null;
    }

    const parsed = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()));
  };

  return (
    <>
      {verifierInfoBlockActionState == ActionState.Enabled && (
        <ActionFieldPanelFullWidth actionState={ActionState.Disabled} style={{ marginTop: 0 }}>
          <WizardTextInputControlledComponent
            label="Verifier"
            name="verifierName"
            disabled
            value={
              verificationInfo?.verifierInfo?.displayName ?? 'Please select the EPD Verifier in Step 1 - General information'
            }
          />
        </ActionFieldPanelFullWidth>
      )}
      <ActionFieldPanelFullWidth actionState={showStubVerifier()}>
        <FieldPanelHeader>Verification report</FieldPanelHeader>
        <HelpBox>
          The verifier will check that the uploaded EPD and filled information in Step 1, Step 2 and Step 3 correspond with
          the verified information in the LCA report and EPD document.
          <br />
          <br />
          After the review, the verifier will upload and approve the EPD. You will be able to see the uploaded verification
          report in this section.
        </HelpBox>
      </ActionFieldPanelFullWidth>
      <ActionFieldPanelFullWidth actionState={showStub()}>
        <FieldPanelHeader>Verification report</FieldPanelHeader>
        <HelpBox>
          In Step 4, the appointed verifier will verify the information that was added in the previous steps
          <br />
          <br />
          After completing the verification, the verifier will upload the verification report and approve the EPD.
          <br /> The verification report will be accessible at this step.
        </HelpBox>
      </ActionFieldPanelFullWidth>
      <CustomActionFieldPanelFullWidth actionState={showUploadBlock()}>
        <div style={{ display: 'flex', position: 'relative', marginBottom: '30px', columnGap: '4px' }}>
          <FieldPanelHeader>Verification report</FieldPanelHeader>
          <TitleIconBar>
            {validationState &&
              validationState.errors &&
              validationState.errors.verificationReports &&
              (verificationInfo?.verificationReports.length ?? 0) == 0 && (
                <TooltipErrorIcon content={validationState.errors.verificationReports} />
              )}
          </TitleIconBar>
        </div>
        {isReportBlockEditable && (
          <HelpBox>
            <span>
              Please upload the verification report in either DOCX, PDF, or XLSX format using the{' '}
              <HyperLink target="_blank" rel="noopener noreferrer" href={EPD_DEVELOPMENT_VERIFICATION_TEMPLATES_URL}>
                provided template
              </HyperLink>
              .<br />
              Multiple reports can be uploaded if necessary.
            </span>
          </HelpBox>
        )}
        {isReportBlockEditable && (
          <FileUpload
            label={'Upload document'}
            icon={ButtonUploadIcon}
            accept=".docx,.pdf,.xlsx"
            validExtensions={['docx', 'pdf', 'xlsx']}
            name={'VerificationReport'}
            onFileUpload={uploadFile}
          />
        )}
        {addVerificationReportFileMutation.isPending && (
          <TextWithAnimatedEllipsis className="mt-2">Uploading file</TextWithAnimatedEllipsis>
        )}
        <FilesList
          files={verificationInfo?.verificationReports}
          onRemoveFile={handleRemoveVerificationReportFile}
          disabled={!isReportBlockEditable}
        />
      </CustomActionFieldPanelFullWidth>
      <ActionFieldPanelFullWidth
        actionState={
          verificationInfo.verificationType === VerificationType.EPDVerification && verificationInfo?.approvalTimestamp
            ? ActionState.Disabled
            : ActionState.Hidden
        }
      >
        <WizardCalendarControlled
          label="Approval timestamp"
          tooltip="This timestamp is automatically generated for the current EPD version when the verifier clicks “Approve EPD”. The approval timestamp neither replaces nor corresponds to the approval date in the verification report."
          disabled
          name="approvalTimestamp"
          value={formatAsUTCDate(verificationInfo?.approvalTimestamp)}
          dateFormat="yy-mm-dd"
        />
      </ActionFieldPanelFullWidth>
    </>
  );
};

const CustomActionFieldPanelFullWidth = styled(ActionFieldPanelFullWidth)`
  grid-row-gap: 5px;
`;

export default Step4;
